/**
 * The grid and plumb line drawn over a postural analysis photo.
 *
 * One component, two uses: the student-profile tab renders it `draggable`,
 * so an instructor can align the line with the photo; the report template
 * renders the exact same drawing `draggable={false}`, from the same frozen
 * `linePositionPercent` the instructor's drag already wrote to the snapshot
 * (Architecture §12.1). Neither reads a clock, fetches anything, or draws
 * randomly — the only thing that varies between the two uses is whether a
 * pointer event is wired up.
 *
 * The grid itself never moves and carries no calibration — it is a visual
 * reference for the eye, not a measuring tool (Doc 6, Postür Analizi design).
 * Colours come from the report's own palette tokens (`currentColor`/CSS
 * variables already in scope on `.pp-report`), never a literal, so the
 * overlay is tinted the same way every other report drawing is.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface PostureOverlayProps {
  /** 0-100, or null before the instructor has aligned anything. */
  linePositionPercent: number | null;
  /** Only meaningful when `draggable` is true. Fired once, on release. */
  onChange?: (percent: number) => void;
  draggable?: boolean;
}

export function PostureOverlay({
  linePositionPercent,
  onChange,
  draggable = false,
}: PostureOverlayProps) {
  const ref = useRef<HTMLDivElement>(null);
  /*
   * While dragging, the line's position is driven by this local state, not
   * by a server round-trip — `onChange` only fires once, on release, so the
   * only way the line can track the pointer in real time is locally. Once
   * the committed value comes back from the server as `linePositionPercent`,
   * control is handed back to the prop so it stays the single source of
   * truth outside of an active drag (and so a drag the parent rejects is not
   * stuck showing a position that was never actually saved).
   */
  const [dragPercent, setDragPercent] = useState<number | null>(null);

  const percentFromClientX = useCallback((clientX: number): number => {
    const box = ref.current?.getBoundingClientRect();
    if (!box || box.width === 0) return 50;
    const raw = ((clientX - box.left) / box.width) * 100;
    return Math.min(100, Math.max(0, raw));
  }, []);

  useEffect(() => {
    if (
      dragPercent !== null &&
      linePositionPercent !== null &&
      Math.abs(linePositionPercent - dragPercent) < 0.01
    ) {
      setDragPercent(null);
    }
  }, [linePositionPercent, dragPercent]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggable || !onChange) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragPercent(percentFromClientX(event.clientX));
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggable || !onChange) return;
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    setDragPercent(percentFromClientX(event.clientX));
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggable || !onChange) return;
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    const final = percentFromClientX(event.clientX);
    setDragPercent(final);
    onChange(final);
  };

  const shown = dragPercent ?? linePositionPercent ?? 50;
  const lineVisible = dragPercent !== null || linePositionPercent !== null;

  return (
    <div
      ref={ref}
      className={
        draggable
          ? 'pp-posture-overlay pp-posture-overlay--draggable'
          : 'pp-posture-overlay'
      }
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      <svg
        className="pp-posture-overlay__svg"
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        {/* The grid: fixed 10% spacing, always full-frame, never moves. */}
        {[10, 20, 30, 40, 50, 60, 70, 80, 90].map((v) => (
          <line
            key={`v${v}`}
            x1={v}
            y1={0}
            x2={v}
            y2={100}
            className="pp-posture-overlay__grid"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {[10, 20, 30, 40, 50, 60, 70, 80, 90].map((v) => (
          <line
            key={`h${v}`}
            x1={0}
            y1={v}
            x2={100}
            y2={v}
            className="pp-posture-overlay__grid"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {/* The plumb line — the one element the instructor positions. */}
        {lineVisible && (
          <line
            x1={shown}
            y1={0}
            x2={shown}
            y2={100}
            className="pp-posture-overlay__line"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      {draggable && !lineVisible && (
        <p className="pp-posture-overlay__hint">Çizgiyi hizalamak için dokunun</p>
      )}
    </div>
  );
}
